import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaces } from '@/contexts/WorkspaceContext';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';
import type { Notification } from '@/types/notifications';

export function useNotifications({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  // Canonical workspace source is the context hook `useWorkspaces` (the plan names it
  // `useWorkspace`, but this project exports `useWorkspaces` from WorkspaceContext;
  // src/hooks/useWorkspaces.ts is the dead legacy hook — MEMORY project_workspace_hook_canonical).
  const { currentWorkspace } = useWorkspaces();
  const wsId = currentWorkspace?.id;
  const qc = useQueryClient();
  const { toast } = useToast();

  const countKey = queryKeys.notifications.unreadCount(wsId, user?.id);
  const listKey = queryKeys.notifications.list(wsId, user?.id);

  const unread = useQuery({
    queryKey: countKey,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('workspace_id', wsId!)
        .is('read_at', null)
        .is('dismissed_at', null);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: enabled && !!user && !!wsId,
    refetchInterval: 60_000,
  });

  const list = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('workspace_id', wsId!)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    enabled: false, // poll-on-open: refetch() is called when the Sheet opens
  });

  const adjustCount = (delta: number) =>
    qc.setQueryData<number>(countKey, (prev) => Math.max(0, (prev ?? 0) + delta));

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .is('read_at', null);
      if (error) throw error;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: countKey });
      const prev = qc.getQueryData<number>(countKey);
      adjustCount(-1);
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(countKey, ctx.prev);
      captureError(err as Error, { feature: 'notifications-mark-read' });
      toast({ title: 'Error', description: 'Failed to mark notification read', variant: 'destructive' });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: countKey });
      qc.invalidateQueries({ queryKey: listKey });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('workspace_id', wsId!)
        .is('read_at', null)
        .is('dismissed_at', null);
      if (error) throw error;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: countKey });
      const prev = qc.getQueryData<number>(countKey);
      qc.setQueryData(countKey, 0);
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(countKey, ctx.prev);
      captureError(err as Error, { feature: 'notifications-mark-all-read' });
      toast({ title: 'Error', description: 'Failed to mark all read', variant: 'destructive' });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: countKey });
      qc.invalidateQueries({ queryKey: listKey });
    },
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: listKey });
      const prevList = qc.getQueryData<Notification[]>(listKey);
      const removed = prevList?.find((n) => n.id === id);
      qc.setQueryData<Notification[]>(listKey, (prev) => (prev ?? []).filter((n) => n.id !== id));
      if (removed && !removed.read_at) adjustCount(-1);
      return { prevList };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prevList) qc.setQueryData(listKey, ctx.prevList);
      captureError(err as Error, { feature: 'notifications-dismiss' });
      toast({ title: 'Error', description: 'Failed to dismiss notification', variant: 'destructive' });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: countKey });
      qc.invalidateQueries({ queryKey: listKey });
    },
  });

  return {
    unreadCount: unread.data ?? 0,
    list,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    dismiss: dismiss.mutate,
  };
}
